import { OrganizationDetailFragment } from '@alice-financial/alice-sdk'

const financialInformationRequirements = [
  'business_profile.annual_revenue.amount',
  'business_profile.annual_revenue.currency',
  'business_profile.annual_revenue.fiscal_year_end',
  'business_profile.estimated_worker_count',
]

const orgToStripeAccount = {
  ein: 'company.tax_id',
  tel: 'company.phone',
  address: 'company.address',
  businessStructure: 'company.structure',
  mcc: 'business_profile.mcc',
  websiteUrl: 'company.url',
  name: 'business_profile.name', // should be set automatically
  legalName: 'company.name', // should be set automatically
}
type OrgAttribute = keyof typeof orgToStripeAccount
export type KYCStatus = 'currently_due' | 'pending_verification' | 'complete'

const isOrgAttribute = (attribute: string): attribute is OrgAttribute =>
  Object.keys(orgToStripeAccount).includes(attribute as OrgAttribute)

const stripeAccountToOrg: Record<string, OrgAttribute> = Object.fromEntries(
  Object.entries(orgToStripeAccount)
    .filter((keyVal): keyVal is [OrgAttribute, string] => isOrgAttribute(keyVal[0]))
    .map(([orgAttribute, stripeAccountAttribute]) => [stripeAccountAttribute, orgAttribute])
)
export const orgAttributeState = (org: OrganizationDetailFragment, attribute: OrgAttribute): KYCStatus => {
  const requirementsPendingVerification =
    org.aliceCardProgram?.companyAccount?.requirementsPendingVerification || []
  const requirementsCurrentlyDue = org.aliceCardProgram?.companyAccount?.currentlyDueRequirements || []
  if (requirementsPendingVerification.find((req) => req.startsWith(orgToStripeAccount[attribute]))) {
    return 'pending_verification'
  }
  if (requirementsCurrentlyDue.find((req) => req.startsWith(orgToStripeAccount[attribute]))) {
    return 'currently_due'
  }
  return 'complete'
}

export const hasAllOrgData = (org: OrganizationDetailFragment) => {
  return Object.keys(orgToStripeAccount)
    .filter(isOrgAttribute)
    .every((attribute) => orgAttributeState(org, attribute) !== 'currently_due')
}

export const hasFinancialInformation = (org: OrganizationDetailFragment) => {
  return (org.aliceCardProgram?.companyAccount?.currentlyDueRequirements || []).every(
    (req) => !financialInformationRequirements.includes(req)
  )
}

export const hasCompanyRepresentative = (org: OrganizationDetailFragment) => {
  return (org.aliceCardProgram?.companyAccount?.currentlyDueRequirements || []).every(
    (req) => !req.startsWith('representative.')
  )
}

export const getCurrentlyDueRequirements = (org: OrganizationDetailFragment) => {
  const requirements = org.aliceCardProgram?.companyAccount?.currentlyDueRequirements || []
  return requirements.reduce((acc, req) => {
    const orgAttribute = stripeAccountToOrg[req]
    if (orgAttribute && isOrgAttribute(orgAttribute)) acc.push(orgAttribute)
    return acc
  }, [] as Array<OrgAttribute>)
}
